/**
 * Publishing Download Component (Alpine.js)
 *
 * Implements a form-gated download system for publishing assets. Users must fill out
 * a Freeform form on their first download attempt, after which a 90-day cookie is set
 * to allow subsequent downloads without re-submitting the form.
 *
 * Flow:
 * 1. User clicks download → check for cookie
 * 2. If cookie exists → trigger download immediately
 * 3. If no cookie → open modal with Freeform "Publishings" form
 * 4. User submits form → set 90-day cookie → trigger download → close modal
 *
 * @module publishingDownload
 * @requires Alpine.js
 * @requires Freeform (Craft CMS plugin)
 *
 * @example
 * // Usage in Twig template
 * <div x-data="publishingDownload()">
 *   <a href="{{ fileUrl }}" @click="handleDownload($event, '{{ fileUrl }}')">
 *     Download
 *   </a>
 * </div>
 *
 * @returns {Object} Alpine.js component instance
 */
export default function publishingDownload() {
	/**
	 * Cookie name for tracking download permission
	 * @constant {string}
	 */
	const COOKIE_NAME = "publishings_download_allowed";

	/**
	 * Cookie expiration duration in days
	 * @constant {number}
	 */
	const COOKIE_DURATION_DAYS = 90;

	return {
		/**
		 * Stores the URL of the file pending download while form is being filled
		 * @type {string|null}
		 */
		pendingDownloadUrl: null,

		/**
		 * Initialize component and set up event listeners
		 * Called automatically by Alpine.js when component is mounted
		 *
		 * @returns {void}
		 */
		init() {
			this.listenForFormSuccess();
			this.obfuscateUrlsIfNeeded();
		},

		/**
		 * Obfuscate download URLs if user doesn't have cookie
		 * Removes href and encodes URL in data attribute to prevent easy access
		 * Only processes links marked with data-dl="protected"
		 *
		 * @returns {void}
		 */
		obfuscateUrlsIfNeeded() {
			if (!this.hasCookie()) {
				setTimeout(() => {
					const links = this.$el.querySelectorAll(
						'[data-dl="protected"]',
					);
					links.forEach((link) => {
						const href = link.getAttribute("href");
						if (href && href !== "") {
							const encoded = this.encodeUrl(href);
							link.setAttribute("data-dl", encoded);
							link.removeAttribute("href");
							link.setAttribute("tabindex", "0");
							link.style.cursor = "pointer";
							link.addEventListener("keydown", (e) => {
								if (e.key === "Enter" || e.key === " ") {
									e.preventDefault();
									this.handleDownload(e);
								}
							});
						}
					});
				}, 100);
			}
		},

		/**
		 * Check if the download permission cookie exists
		 *
		 * @returns {boolean} True if cookie exists and user has download permission
		 */
		hasCookie() {
			const cookies = document.cookie.split("; ");
			return cookies.some((cookie) =>
				cookie.startsWith(`${COOKIE_NAME}=`),
			);
		},

		/**
		 * Set the download permission cookie with 90-day expiration
		 * Cookie is set as SameSite=Lax for security and path=/ for site-wide access
		 *
		 * @returns {void}
		 */
		setCookie() {
			const maxAge = COOKIE_DURATION_DAYS * 24 * 60 * 60;
			document.cookie = `${COOKIE_NAME}=true; max-age=${maxAge}; path=/; SameSite=Lax`;
		},

		/**
		 * Decode base64 encoded URL
		 *
		 * @param {string} encoded - Base64 encoded URL
		 * @returns {string} Decoded URL
		 */
		decodeUrl(encoded) {
			try {
				return atob(encoded);
			} catch (e) {
				console.error("Failed to decode URL:", e);
				return encoded;
			}
		},

		/**
		 * Encode URL to base64
		 *
		 * @param {string} url - URL to encode
		 * @returns {string} Base64 encoded URL
		 */
		encodeUrl(url) {
			try {
				return btoa(url);
			} catch (e) {
				console.error("Failed to encode URL:", e);
				return url;
			}
		},

		/**
		 * Handle download button click
		 * Checks for cookie and either downloads immediately or opens the form modal
		 *
		 * @param {Event} event - The click event
		 * @param {string|null} downloadUrl - The URL of the file to download (optional, reads from data attribute if not provided)
		 * @returns {void}
		 *
		 * @example
		 * <a @click="handleDownload($event)">Download PDF</a>
		 */
		handleDownload(event, downloadUrl = null) {
			event.preventDefault();

			let url = downloadUrl;

			if (!url) {
				const element = event.currentTarget;
				const href = element.getAttribute("href");
				const encodedUrl = element.getAttribute("data-dl");

				if (href) {
					url = href;
				} else if (encodedUrl && encodedUrl !== "protected") {
					url = this.decodeUrl(encodedUrl);
				}
			}

			if (!url) {
				console.error("No download URL found");
				return;
			}

			if (this.hasCookie()) {
				this.triggerDownload(url);
			} else {
				this.pendingDownloadUrl = url;
				this.openModal();
			}
		},

		/**
		 * Open the publishing download modal
		 * Dispatches a custom event that the modal component listens for
		 *
		 * @returns {void}
		 */
		openModal() {
			window.dispatchEvent(new CustomEvent("open-publishing-modal"));
		},

		/**
		 * Close the publishing download modal
		 * Dispatches a custom event that the modal component listens for
		 *
		 * @returns {void}
		 */
		closeModal() {
			window.dispatchEvent(new CustomEvent("close-publishing-modal"));
		},

		/**
		 * Trigger file download programmatically
		 * Creates a temporary anchor element, clicks it, and removes it from DOM
		 *
		 * @param {string} url - The URL of the file to download
		 * @returns {void}
		 */
		triggerDownload(url) {
			const link = document.createElement("a");
			link.href = url;
			link.download = "";
			link.target = "_blank";
			link.rel = "noopener noreferrer";
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
		},

		/**
		 * Listen for Freeform form successful submission
		 * When form submits successfully, sets cookie, triggers download, and closes modal
		 *
		 * Note: Only acts if pendingDownloadUrl is set (modal is open with pending download)
		 * The event is fired by Freeform's AJAX submission system
		 *
		 * @returns {void}
		 */
		listenForFormSuccess() {
			document.addEventListener("freeform-render-success", () => {
				if (this.pendingDownloadUrl) {
					this.setCookie();
					this.triggerDownload(this.pendingDownloadUrl);
					this.pendingDownloadUrl = null;

					setTimeout(() => {
						this.closeModal();
					}, 1500);
				}
			});
		},
	};
}
